import { debounce } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Col, Container, Modal, Row, Spinner } from 'react-bootstrap';
import { BsSliders } from 'react-icons/bs';
import InfiniteScroll from 'react-infinite-scroller';
import { useParams } from 'react-router-dom';
import { CategoryBreadcrumbNavigation } from '../../components/breadcrumbNavigation';
import ComparePanel from '../../components/comparePanel';
import SortingPanel from '../../components/dropdowns/products';
import FilterPanel from '../../components/filterPanel';
import ProductCard from '../../components/productCard';
import Constants from '../../helpers/Constants';
import { Category, Criteria } from '../../models/category';
import { Product } from '../../models/product';
import apiService from '../../services/ApiService';
import { useCurrentCategoryIds } from '../../services/useCurrentCategoryIds';
import './categoryPage.css';

const CategoryPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isFiltering, setIsFiltering] = useState(false);
  const [categoryData, setCategoryData] = useState<Category>({
    items: [],
    itemCount: 0,
    pageCount: 0,
    id: '',
    name: '',
    filter: {
      attributes: [],
    },
    sortings: {},
  });

  const { id } = useParams();

  const [currCateIds, setCurrCateIds] = useState(useCurrentCategoryIds(id));

  const initialCriteria: Criteria = {
    attributes: {},
    sorting: '1',
    keyword: '',
    page: 1,
    inStockOnly: false,
    id: id!,
  };
  const [criteria, setCriteria] = useState<Criteria>(initialCriteria);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [isApplyFiltering, setIsApplyFiltering] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const getCategoryData = useRef(
    debounce((params: any, isLoading: boolean, categoryData: Category) => {
      setIsFiltering(true);
      apiService.get(Constants.ApiPath.CategoryPage, params).then((result) => {
        if (result) {
          let data: Category = {
            items: categoryData.items.concat(result.data.products),
            itemCount: result.data.itemCount,
            pageCount: result.data.pageCount,
            id: result.data.id,
            name: result.data.name,
            filter: categoryData.filter,
            sortings: categoryData.sortings,
          };

          if (isLoading) {
            data.sortings = result.data.sortings;
            data.filter.attributes = result.data.attributes ?? [];
          }

          if (params.page == 1) {
            data.items = result.data.products;
          }

          setCategoryData(data);
          setIsLoading(false);
          setIsFiltering(false);
          setIsLoadingMore(false);
        }
      });
    }, 900)
  ).current;

  useEffect(() => {
    if (!isApplyFiltering) {
      return;
    }

    var params: any = {
      sorting: criteria.sorting,
      inStockOnly: criteria.inStockOnly,
      page: criteria.page,
      id: criteria.id,
    };
    Object.keys(criteria.attributes)
      .filter((name) => criteria.attributes[name].length > 0)
      .forEach((name, i) => {
        params = {
          ...params,
          [`attributes[${i}].key`]: name,
          [`attributes[${i}].value`]: criteria.attributes[name],
        };
      });
    getCategoryData(params, isLoading, categoryData);
  }, [criteria, isApplyFiltering]);

  useEffect(() => {
    setIsLoading(true);
    setSelectedProducts([]);
    const filter: Criteria = { ...initialCriteria, id: id! };
    setCriteria(filter);
  }, [id]);

  const handleCompareCheckboxChange = (event: any) => {
    const id = event.target.value;
    const product = categoryData.items.filter((p) => p.sku === id)[0];
    const products: Product[] = event.target.checked
      ? [...selectedProducts, product]
      : selectedProducts.filter((p) => p.sku !== id);
    if (products.length > Constants.MaxProductsCompared) {
      event.target.checked = false;
      return;
    }
    setSelectedProducts(products);
  };

  const handleClearSelectedProducts = () => {
    setSelectedProducts([]);
  };

  const handleUnSelectProduct = (sku: string) => {
    setSelectedProducts((products) => products.filter((p) => p.sku !== sku));
  };

  const handleFilterValueChange = (filter: any) => {
    setCriteria((prevData) => {
      let attributes = prevData.attributes;
      if (filter.name === 'keyword') {
        setIsApplyFiltering(false);
        return {
          ...prevData,
          keyword: filter.value,
          page: 1,
        };
      } else if (filter.name === 'inStockOnly') {
        return {
          ...prevData,
          inStockOnly: filter.value,
          page: 1,
        };
      } else {
        let values: string[] = prevData.attributes[filter.name] ?? [];
        values = filter.checked
          ? [...values, filter.value]
          : values.filter((p) => p !== filter.value);
        attributes[filter.name] = values.filter((n, i) => values.indexOf(n) === i);
      }
      const value: Criteria = {
        ...prevData,
        attributes,
        page: 1,
      };
      return value;
    });
  };

  const handleFilterApply = () => {
    setShowFilterPanel(false);
    setIsApplyFiltering(true);
  };

  const handleSortingChange = (value: string) => {
    if (value) {
      setCriteria((prevData) => {
        return {
          ...prevData,
          sorting: value,
          page: 1,
        };
      });
    }
  };

  const handleLoadMore = () => {
    if (!isLoadingMore) {
      setIsLoadingMore(true);
      setCriteria((prevData) => {
        console.log('setCriteria', prevData.page);
        return {
          ...prevData,
          page: prevData.page + 1,
        };
      });
    }
  };

  const loader = (
    <Container className="category-page-infinite-loading" key="category-page-loading">
      <Spinner animation="border" />
      <p>Loading...</p>
    </Container>
  );

  return isLoading ? (
    <Container className="category-page-loading" key="category-page-loading">
      <div>
        <Spinner animation="border" />
        <p>Loading...</p>
      </div>
    </Container>
  ) : (
    <>
      {isFiltering && (
        <Container className="category-page-filtering" fluid>
          <Spinner animation="border" />
          <p>Loading...</p>
        </Container>
      )}
      <Container fluid>
        <CategoryBreadcrumbNavigation categoryTagId={categoryData.id} />
        {/* <Row>
          <Col className="category-page-name-container">
            <span id="category-page-name">{categoryData.name}</span>
          </Col>
        </Row> */}
        <Row>
          <Col className="category-page-filter-menu">
            <div className="filter-panel-fullscreen">
              <FilterPanel
                filter={categoryData.filter}
                onChange={(val) => {
                  setIsApplyFiltering(true);
                  handleFilterValueChange(val);
                }}
                index={1}
                criteria={criteria}
              />
            </div>
          </Col>
          <Col>
            <section id="category-page-upper">
              <span>{categoryData.itemCount} Items Found</span>
              <span
                onClick={() => {
                  setIsApplyFiltering(false);
                  setShowFilterPanel(!showFilterPanel);
                }}
              >
                <BsSliders />
              </span>
              <Modal
                show={showFilterPanel}
                fullscreen={true}
                onHide={() => {
                  setShowFilterPanel(false);
                }}
                scrollable={true}
              >
                <Modal.Header closeButton>
                  <Modal.Title>SORT & FILTER</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <SortingPanel
                    selectedValue={criteria.sorting}
                    items={categoryData.sortings}
                    onSelected={(val) => {
                      setIsApplyFiltering(false);
                      handleSortingChange(val);
                    }}
                  />
                  <FilterPanel
                    filter={categoryData.filter}
                    onChange={(val) => {
                      setIsApplyFiltering(false);
                      handleFilterValueChange(val);
                    }}
                    index={2}
                    criteria={criteria}
                  />
                </Modal.Body>
                <Modal.Footer className="filter-panel-buttons">
                  <button
                    id="filter-panel-cancel-btn"
                    onClick={() => {
                      setShowFilterPanel(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button id="filter-panel-btn" onClick={handleFilterApply}>
                    Apply
                  </button>
                </Modal.Footer>
              </Modal>
            </section>
            <section id="fullscreen-category-page-upper">
              <div className="items-found-bar">
                <span>{categoryData.itemCount} Items Found</span>
                <SortingPanel
                  selectedValue={criteria.sorting}
                  items={categoryData.sortings}
                  onSelected={(val) => {
                    setIsApplyFiltering(true);
                    handleSortingChange(val);
                  }}
                />
              </div>
            </section>
            <InfiniteScroll
              initialLoad={false}
              loadMore={handleLoadMore}
              hasMore={criteria.page < categoryData.pageCount}
              loader={loader}
            >
              <section
                id="category-page-lower"
                key="category-page-lower"
                className="product-cards-container"
              >
                {categoryData.items.map((model: Product) => {
                  const selected =
                    selectedProducts.findIndex((product) => product.sku === model.sku) >= 0;
                  return (
                    <ProductCard
                      model={model}
                      key={model.id}
                      onChange={handleCompareCheckboxChange}
                      selected={selected}
                      compareable={true}
                    />
                  );
                })}
              </section>
            </InfiniteScroll>
          </Col>
        </Row>
      </Container>
      <section>
        <ComparePanel
          selectedProducts={selectedProducts}
          onClear={handleClearSelectedProducts}
          onRemove={(sku) => handleUnSelectProduct(sku)}
        />
      </section>
    </>
  );
};

export default CategoryPage;
